import { debounce, useEventCallback } from '@mui/material';
import { isSameOrNestedPath } from '@provision/portal-routes';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { KEYBOARD_OPEN_DEBOUNCE_MS } from 'src/components/Navbar/DesktopDrawer/InboxPane/inbox-pane.constants';
import { useGroupRules } from 'src/graphql/entities/group-rules/group-rules';
import { UserGroupRules } from 'src/graphql/entities/group-rules/group-rules.types';
import { Notification } from 'src/graphql/entities/notification';
import {
  getNotificationConversationFriendlyId,
  getNotificationConversationSubject,
  getNotificationEntityName,
  getNotificationFullPagePath,
  getNotificationOpenBehavior,
  getNotificationPreviewPath,
  getNotificationTargetPath,
  resolveNotificationTarget,
} from 'src/graphql/entities/notification/notification-target';
import { useSelectedPartner } from 'src/graphql/entities/partner';
import { EntityNames } from 'src/graphql/types';
import { getPartnerFriendlyIdentifier } from 'src/routes/hooks';
import { useGlobalStore } from 'src/store';
import { useSelectedConversation } from 'src/store/selected-conversation';
import { type TabEntity, TabType, useGlobalTabs } from 'src/utils/hooks/use-global-tabs';
import { useNavigateRoute } from 'src/utils/routing';
import { Platform } from 'src/utils/system';

export type NotificationClickModifiers = {
  shiftKey: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
};

function getNotificationTab(
  notification: Notification,
  entityName: EntityNames | null,
  conversationFriendlyId: string | null,
): TabEntity | null {
  switch (entityName) {
    case EntityNames.Conversation: {
      const conversationData = notification.parsedPayload?.conversation;
      if (!conversationFriendlyId || !conversationData) {
        return null;
      }

      return {
        type: TabType.Conversation,
        entity: {
          id: notification.entityId,
          conversationId: notification.entityId,
          friendlyId: conversationFriendlyId,
          partnerId: conversationData.partnerId,
          subject: getNotificationConversationSubject(notification),
          unseenCount: 0,
        },
      };
    }
    case EntityNames.Site:
      return {
        type: TabType.Site,
        entity: {
          id: notification.entityId,
          addressId: notification.entityId,
          partnerId: notification.partnerId,
        },
      };
    case EntityNames.Client:
      return {
        type: TabType.Client,
        entity: {
          id: notification.entityId,
          clientId: notification.entityId,
          partnerId: notification.partnerId,
        },
      };
    default:
      return null;
  }
}

export const useNotificationNavigation = ({
  markOneAsRead,
}: {
  markOneAsRead: (notification: Notification) => void;
}) => {
  const { navigate, partnerPrefix, pathnameWithoutPrefix } = useNavigateRoute();
  const routerNavigate = useNavigate();
  const { partners } = useSelectedPartner();
  const setSelectedPartnerId = useGlobalStore.use.setSelectedPartnerId();
  const { conversationId: selectedConversationId } = useSelectedConversation();
  const { openInTab } = useGlobalTabs();
  const { groupRules } = useGroupRules();
  const canUseTabs = groupRules[UserGroupRules.AccessToTabsInProvision];

  // the Projects grid is gated behind a per-partner PartnerGroupRule, which the
  // gate can only evaluate with a partner selected — in multi-tenancy mode (or
  // browsing a different partner's notifications) that's not guaranteed to be
  // the notification's own partner, so build its path with that partner's own
  // prefix rather than trusting whatever is currently selected
  const getProjectPeekPath = useCallback(
    (peekPath: string, notification: Notification) => {
      const targetPartner = partners?.[notification.partnerId] ?? null;
      const friendlyId = targetPartner
        ? getPartnerFriendlyIdentifier(targetPartner)
        : String(notification.partnerId);
      return `/${friendlyId}${peekPath}`;
    },
    [partners],
  );

  const navigateToTarget = useCallback(
    (notification: Notification) => {
      const { opensAs, peekPath } = resolveNotificationTarget(notification);

      if (opensAs === 'none') return;

      if (opensAs === 'gridPeek') {
        if (peekPath) {
          setSelectedPartnerId(notification.partnerId);
          routerNavigate(getProjectPeekPath(peekPath, notification));
        }
        return;
      }

      navigate(getNotificationPreviewPath(notification));
    },
    [getProjectPeekPath, navigate, routerNavigate, setSelectedPartnerId],
  );

  const openInBrowserTab = useCallback(
    (notification: Notification) => {
      // a browser tab is its own window with no pane beside it, so the record
      // gets the whole page; projects only ever render peeked on their grid
      const { opensAs, peekPath, fullPagePath } = resolveNotificationTarget(notification);
      const path = fullPagePath ?? peekPath;

      if (!path) return;

      const prefixedPath =
        opensAs === 'gridPeek'
          ? getProjectPeekPath(path, notification)
          : `${partnerPrefix ? `/${partnerPrefix}` : ''}${path}`;

      window.open(prefixedPath, '_blank');
    },
    [getProjectPeekPath, partnerPrefix],
  );

  const openInInternalTab = useCallback(
    (notification: Notification) => {
      const { entityName, conversationFriendlyId } = resolveNotificationTarget(notification);
      const tab = canUseTabs
        ? getNotificationTab(notification, entityName, conversationFriendlyId)
        : null;

      // projects and platform users have no tab type — leave the modifier
      // meaningless rather than swallowing the click
      if (!tab) {
        navigateToTarget(notification);
        return;
      }

      openInTab(tab, undefined, false);
    },
    [canUseTabs, navigateToTarget, openInTab],
  );

  // keyboard-only: the arrow-key highlight moves instantly, but the actual
  // open (navigate + markOneAsRead) waits for the user to stop navigating.
  // The debounced function must stay the SAME instance across renders (via
  // useEventCallback, not a useMemo keyed on markOneAsRead/navigateToTarget) —
  // otherwise every re-render triggered by the arrow-key highlight spawns a
  // fresh debounce with its own timer, and rapid presses fire once per press
  // instead of once after the user stops.
  const openTarget = useEventCallback((notification: Notification) => {
    markOneAsRead(notification);
    navigateToTarget(notification);
  });

  const openTargetDebounced = useMemo(
    () => debounce(openTarget, KEYBOARD_OPEN_DEBOUNCE_MS),
    [openTarget],
  );

  const handleItemClick = useCallback(
    (notification: Notification, modifiers?: NotificationClickModifiers) => {
      markOneAsRead(notification);

      // the modifier paths run straight off the click: window.open needs the
      // user gesture the debounce would have expired
      if (modifiers?.shiftKey) {
        openInBrowserTab(notification);
        return;
      }

      if (Platform.isMacOS ? modifiers?.metaKey : modifiers?.ctrlKey) {
        openInInternalTab(notification);
        return;
      }

      // a plain click opens immediately; cancel any pending keyboard-debounced
      // open so a stale arrow-key navigation can't fire after this one
      openTargetDebounced.clear();
      navigateToTarget(notification);
    },
    [
      markOneAsRead,
      navigateToTarget,
      openInBrowserTab,
      openInInternalTab,
      openTargetDebounced,
    ],
  );

  const isRowSelected = useCallback(
    (notification: Notification) => {
      const entityName = getNotificationEntityName(notification.entity);

      // projects skip the preview route entirely and open straight on their
      // grid peek — compare against that path directly
      if (getNotificationOpenBehavior(entityName) === 'gridPeek') {
        const peekPath = getNotificationTargetPath({
          entityName,
          entityId: notification.entityId,
        });
        return !!peekPath && peekPath === pathnameWithoutPrefix;
      }

      // the preview route is where every other click lands now, and the primary
      // source of truth for which row is "open"
      if (getNotificationPreviewPath(notification) === pathnameWithoutPrefix) {
        return true;
      }

      if (entityName === EntityNames.Conversation) {
        // the store is fed by the route, so this also holds under a custom view
        const friendlyId = getNotificationConversationFriendlyId(notification);
        return !!friendlyId && selectedConversationId === friendlyId;
      }

      // the full page is where the user may have expanded the record out of
      // the preview — still this notification's record
      const fullPagePath = getNotificationFullPagePath({
        entityName,
        entityId: notification.entityId,
      });
      return !!fullPagePath && isSameOrNestedPath(pathnameWithoutPrefix, fullPagePath);
    },
    [pathnameWithoutPrefix, selectedConversationId],
  );

  return { navigateToTarget, handleItemClick, openTargetDebounced, isRowSelected };
};
